import { AFFIRM_WIDGET_TEST_VARIANTS, WARRANTY_CUSTOM_LINE_ITEM_TYPE } from '../../utils/common/constants';

const { calculateTotalGiftCards } = require('../../utils');

const MERCHANT_NAME = 'Purple LLC';
const POSTBACK_METHOD = 'POST';
const CURRENCY = 'USD';
const FINANCING_PROGRAM = '';
const METADATA_MODE = 'modal';
const ITEM_IMAGE_URL = 'https://www.purple.com';
const ITEM_URL = 'https://www.purple.com';

const buildMerchantInfo = (merchantName) => ({
  merchant: {
    user_confirmation_url: '',
    user_cancel_url: '',
    user_confirmation_url_action: POSTBACK_METHOD,
    name: merchantName,
    exchange_lease_enabled: true,
  },
});

const buildShippingInfo = (cart) => ({
  shipping: {
    name: {
      first: cart.shippingAddress.firstName,
      last: cart.shippingAddress.lastName,
    },
    address: {
      line1: cart.shippingAddress.streetName,
      line2: cart.shippingAddress.additionalStreetInfo,
      city: cart.shippingAddress.city,
      state: cart.shippingAddress.state,
      zipcode: cart.shippingAddress.postalCode,
      country: cart.shippingAddress.country,
    },
    phone_number: cart.shippingAddress.mobile,
    email: cart.shippingAddress.email,
  },
});

const buildBillingInfo = (cart) => {
  const address = cart.billingAddress || cart.shippingAddress;
  return {
    billing: {
      name: {
        first: address.firstName,
        last: address.lastName,
      },
      address: {
        line1: address.streetName,
        line2: address.additionalStreetInfo,
        city: address.city,
        state: address.state,
        zipcode: address.postalCode,
        country: address.country,
      },
      phone_number: cart.shippingAddress.mobile,
      email: cart.shippingAddress.email,
    },
  };
};

const buildItemInfo = (lineItem, locale) => ({
  display_name: lineItem.name[locale],
  sku: lineItem.variant.sku,
  unit_price: lineItem.price.value.centAmount,
  qty: lineItem.quantity,
  item_image_url: lineItem.variant.images?.[0]?.url,
  item_url: lineItem.variant.images?.[0]?.url,
  leasable: true,
});

const buildRecyclingItemInfo = (customLineItem, locale) => ({
  display_name: customLineItem.name[locale],
  sku: customLineItem.id,
  unit_price: customLineItem.money.centAmount,
  qty: customLineItem.quantity,
  item_image_url: ITEM_IMAGE_URL,
  item_url: ITEM_URL,
  leasable: false,
});

const builWarrantyItemInfo = (customLineItem, locale) => ({
  display_name: customLineItem.name[locale],
  sku: customLineItem.id,
  unit_price: customLineItem.money.centAmount,
  qty: customLineItem.quantity,
  item_image_url: ITEM_IMAGE_URL,
  item_url: ITEM_URL,
  leasable: true,
});

const buildItems = (cart, locale) => {

  const affirmPromoABTest = window?.purTogglesEnabled('affirm_promo') ?? false;

  const affirmLineItems = cart.lineItems.map((lineItem) =>
    buildItemInfo(lineItem, locale),
  );
  const affirmRecyclingLineItems = cart.customLineItems
    .filter((customLineItem) => customLineItem.slug === 'custom_recyclingFee')
    .map((customLineItem) => buildRecyclingItemInfo(customLineItem, locale));

  const warrantiesLineItems = cart.customLineItems
    .filter(
      (customLineItem) =>
        customLineItem.custom?.fields?.type === WARRANTY_CUSTOM_LINE_ITEM_TYPE,
    )
    .map((customLineItem) => builWarrantyItemInfo(customLineItem, locale));
  
  const affirmPromoTestLineItems = [{
    display_name: AFFIRM_WIDGET_TEST_VARIANTS.name,
    sku:
      affirmPromoABTest
        ? AFFIRM_WIDGET_TEST_VARIANTS.test.variant
        : AFFIRM_WIDGET_TEST_VARIANTS.control.variant,
    unit_price: cart.taxedPrice.totalGross.centAmount,
  }];
  return [
    ...affirmLineItems,
    ...affirmRecyclingLineItems,
    ...warrantiesLineItems,
    ...affirmPromoTestLineItems,
  ];
};

const buildMeta = (cart) => ({
  metadata: {
    shipping_type: cart?.shippingInfo?.shippingMethodName || 'Default',
    mode: METADATA_MODE,
  },
});

const getShippingAmount = (cart) =>
  cart.customLineItems
    .filter(
      (customLineItem) => customLineItem.custom?.fields?.netsuite_shipping_name,
    )
    .reduce((curr, acc) => curr + acc.totalPrice.centAmount, 0);

const buildTotal = (cart) => {
  const totalGiftCards = calculateTotalGiftCards(cart);

  const cartTotal = cart.taxedPrice.totalGross.centAmount;

  const total = cartTotal - totalGiftCards;

  return total <= 0 ? 0 : total;
};

const buildOrderInfo = (cart, affirmFinancingProgramme) => ({
  order_id: cart.id,
  currency: CURRENCY,
  financing_program: affirmFinancingProgramme || FINANCING_PROGRAM,
  shipping_amount: getShippingAmount(cart),
  tax_amount:
    cart.taxedPrice.totalGross.centAmount - cart.taxedPrice.totalNet.centAmount,
  total: buildTotal(cart),
});

const buildDiscounts = (cart) => {
  const { giftCards = [] } = cart;

  if (!giftCards.length) {
    return {};
  }

  return {
    discounts: giftCards.reduce(
      (acc, giftCard) => ({
        ...acc,
        [giftCard.giftCardNumber]: {
          discount_amount: giftCard.amount.centAmount,
          discount_display_name: giftCard.giftCardNumber,
        },
      }),
      {},
    ),
  };
};

const buildAffirmCheckoutObject = (
  cart,
  affirmFinancingProgramme,
  items,
) => ({
  ...buildMerchantInfo(MERCHANT_NAME),
  ...buildShippingInfo(cart),
  ...buildBillingInfo(cart),
  ...{ items },
  ...buildDiscounts(cart),
  ...buildMeta(cart),
  ...buildOrderInfo(cart, affirmFinancingProgramme),
});

const buildAffirmObject = (currentCart, affirmFinancingProgramme, locale) =>
  buildAffirmCheckoutObject(currentCart, affirmFinancingProgramme, buildItems(currentCart, locale));

const affirmService = {
  buildAffirmObject,
};

export default affirmService;
